import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Button from "@/components/ui/Button.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { callFunctionRaw } from "@/lib/callFunction.js";
import supabase from "@/services/supabaseClient";
import {
  clearPasswordMigrationSignal,
  hasPasswordConfigured,
} from "@/features/security/accountSecurityState.js";
import PasswordRequirementChecklist from "@/features/security/PasswordRequirementChecklist.jsx";
import {
  PASSWORD_RULES_ATTR,
  generateCompliantPassword,
  meetsPasswordPolicy,
} from "@/features/security/passwordPolicy.js";
import {
  checkStagingAccess,
  normalizeStagingEmail,
  writeCachedStagingAccess,
} from "@/services/stagingAccessService.js";

const ACCOUNT_RESET_STAGING_HANDOFF_KEY = "afrimed:account-reset:staging-handoff:v1";

function PasswordField({
  id,
  label,
  name,
  value,
  onChange,
  autoComplete,
  disabled = false,
}) {
  const { t } = useLanguage();
  const [visible, setVisible] = React.useState(false);

  return (
    <div className="relative">
      <TextField56
        id={id}
        label={label}
        name={name}
        value={value}
        disabled={disabled}
        onChange={onChange}
        autoComplete={autoComplete}
        type={visible ? "text" : "password"}
        tone="surface"
        className="pr-10"
        inputProps={{
          minLength: 8,
          passwordrules: PASSWORD_RULES_ATTR,
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((next) => !next)}
        aria-label={
          visible
            ? t("settings.password.hidePassword", "Hide password")
            : t("settings.password.showPassword", "Show password")
        }
        className="absolute bottom-3 right-3 flex h-7 w-7 items-center justify-center"
      >
        <img
          src={visible ? "/icons/list-items/visibility.svg" : "/icons/list-items/visibility_off.svg"}
          alt=""
          className="h-5 w-5"
        />
      </button>
    </div>
  );
}

function parseAccountResetHandoff() {
  if (typeof window === "undefined") return null;
  const search = new URLSearchParams(window.location.search || "");
  const token = String(search.get("token") || "").trim();
  if (token) return { token };
  return null;
}

function getAuthErrorDebug(error) {
  if (!error) return {};
  return {
    name: error.name,
    code: error.code,
    status: error.status,
    message: error.message,
  };
}

function authErrorText(error) {
  return [
    error?.code,
    error?.name,
    error?.message,
    error?.status ? String(error.status) : "",
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function isSamePasswordError(error) {
  const text = authErrorText(error);
  return (
    text.includes("same") ||
    text.includes("different from the old password") ||
    text.includes("already in use") ||
    text.includes("password_reuse") ||
    text.includes("same_password")
  );
}

function isExpiredSessionError(error) {
  const text = authErrorText(error);
  return (
    text.includes("authsessionmissing") ||
    text.includes("session") ||
    text.includes("jwt") ||
    text.includes("token") ||
    text.includes("expired") ||
    text.includes("invalid_grant") ||
    text.includes("not authenticated") ||
    error?.status === 401 ||
    error?.status === 403
  );
}

function isWeakPasswordError(error) {
  const text = authErrorText(error);
  return (
    text.includes("weak") ||
    text.includes("password_policy") ||
    text.includes("password should") ||
    text.includes("password must") ||
    text.includes("minimum")
  );
}

function mapCreatePasswordSaveError(error, t) {
  if (isSamePasswordError(error)) {
    return t(
      "settings.password.samePasswordError",
      "This password is already in use. Please choose a different password.",
    );
  }
  if (isExpiredSessionError(error)) {
    return t(
      "settings.password.sessionExpiredError",
      "Your session has expired. Please sign in again and create your password.",
    );
  }
  if (isWeakPasswordError(error)) {
    return t(
      "settings.password.weakPasswordError",
      "Please choose a less predictable password. Try adding more words, numbers, or mixed characters.",
    );
  }
  return t(
    "settings.password.saveError",
    "We could not save your password right now. Please try again.",
  );
}

function logPasswordSaveError(error) {
  if (!import.meta.env.DEV) return;
  // Keep production copy user-safe while making local Supabase failures inspectable.
  console.error("[AfriMed auth] Supabase password save failed", getAuthErrorDebug(error));
}

function getPasswordRequirementStatus(password = "") {
  const value = String(password || "");
  return {
    uppercase: /[A-Z]/.test(value),
    number: /\d/.test(value),
    lowercase: /[a-z]/.test(value),
    symbol: /[^A-Za-z0-9]/.test(value),
    minLength: value.length >= 8,
  };
}

function PasswordRequirementSentence({ password }) {
  const checks = getPasswordRequirementStatus(password);
  const validClass = "font-medium text-green-700";
  const mutedClass = "text-on-surface-var";
  const wordClass = (valid) => (valid ? validClass : mutedClass);
  const allValid = Object.values(checks).every(Boolean);

  return (
    <div className="text-center text-body-small leading-relaxed">
      <p className={allValid ? "font-medium text-green-700" : "text-on-surface-var"}>
        Use a strong password consisting of{" "}
        <span className={wordClass(checks.uppercase)}>Uppercase</span>
      </p>
      <p>
        <span className={wordClass(checks.number)}>Number</span>
        <span className={mutedClass}> • </span>
        <span className={wordClass(checks.lowercase)}>Lowercase</span>
        <span className={mutedClass}> • </span>
        <span className={wordClass(checks.symbol)}>Symbol</span>
        <span className={mutedClass}> • </span>
        <span className={wordClass(checks.minLength)}>Minimum 8 characters</span>
      </p>
    </div>
  );
}

export default function AccountPasswordLifecyclePanel({
  context = "settings",
  intent = "auto",
  hasPasswordOverride,
  userEmailOverride = "",
  titleOverride = "",
  copyOverride = "",
  createSignOutNoticeOverride,
  submitLabelOverride = "",
  leadingFormContent = null,
  trailingFormContent = null,
  secondarySubmitAction = null,
  submitLeadingIcon = null,
  submitRowClassName = "",
  showCopyPasswordAction = false,
  canSubmitExtra = true,
  onPasswordSubmit,
  onPasswordSubmitSuccessMessage = "",
  enableResetHandoff = true,
  titleGroupClassName = "",
  titleClassName = "text-headline-small text-on-surface",
  requirementsPresentation = "checklist",
  requirementsRailClassName = "",
  onUnlocked,
  onCreated,
  onCreatePasswordSuccess,
  clearPasswordMigrationOnSuccess = true,
  className = "",
  resetBasePath = "/settings/patient/login-security/change-password",
  loginSecurityPath = "/settings/patient/login-security",
  preserveAccountSessionAfterUpdate = false,
}) {
  const nav = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const auth = useAuth() || {};
  const user = auth.user || null;
  const userEmail = String(userEmailOverride || user?.email || "").trim();
  const [passwordCreatedHere, setPasswordCreatedHere] = React.useState(false);
  const baseHasPassword =
    typeof hasPasswordOverride === "boolean"
      ? hasPasswordOverride
      : hasPasswordConfigured(user);
  const hasPassword = passwordCreatedHere || baseHasPassword;
  const mode =
    intent === "create"
      ? "create"
      : hasPassword && context === "ehr"
      ? "unlock"
      : hasPassword
        ? "change"
        : intent === "change"
          ? "create"
          : "create";

  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState("");
  const [resetMode, setResetMode] = React.useState(null);
  const [resetEmail, setResetEmail] = React.useState(() => userEmail);
  const [resetSessionReady, setResetSessionReady] = React.useState(false);
  const [resetToken, setResetToken] = React.useState("");
  const [showResetConfirmationHelper, setShowResetConfirmationHelper] = React.useState(false);
  const postCreateSignOutTimerRef = React.useRef(null);

  React.useEffect(() => {
    setResetEmail((current) => current || String(user?.email || "").trim());
  }, [user?.email]);

  React.useEffect(() => {
    if (!enableResetHandoff) return undefined;
    if (context === "ehr") return undefined;
    const recovery = parseAccountResetHandoff();
    if (!recovery) return undefined;

    setResetMode("confirm");
    setResetSessionReady(true);
    setResetToken(recovery.token || "");
    setError("");
  }, [context, enableResetHandoff, location.pathname, location.search, t]);

  React.useEffect(() => {
    if (resetMode !== "sent") {
      setShowResetConfirmationHelper(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setShowResetConfirmationHelper(true);
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [resetMode]);

  React.useEffect(() => {
    return () => {
      if (postCreateSignOutTimerRef.current) {
        window.clearTimeout(postCreateSignOutTimerRef.current);
      }
    };
  }, []);

  const title = titleOverride || (
    mode === "create"
      ? t("settings.password.createTitle", "Create your password")
      : mode === "change"
        ? t("settings.password.changeTitle", "Change your password")
        : t("settings.subflows.patientEhr.unlockTitle", "Unlock My Health Record")
  );

  const copy =
    copyOverride === null
      ? ""
      : copyOverride ||
        (mode === "create"
          ? t(
              "settings.password.createCopy",
              "Use at least 8 characters with uppercase, lowercase, number and symbol.",
            )
          : mode === "unlock"
            ? t(
                "settings.subflows.patientEhr.unlockBody",
                "Enter your AfriMed™ password to open your health record.",
              )
            : t(
                "settings.password.changeCopy",
                "Use at least 8 characters with uppercase, lowercase, number and symbol.",
              ));

  const cta = submitLabelOverride || (
    mode === "create"
      ? t("settings.password.createAction", "Create password")
      : mode === "change"
        ? t("settings.password.updateAction", "Update password")
        : t("settings.subflows.patientEhr.unlockAction", "Unlock record")
  );

  const currentLabel =
    mode === "unlock"
      ? t("settings.subflows.patientEhr.unlockPasswordLabel", "Password")
      : t("settings.password.currentLabel", "Current password");

  const canSubmitExtraResolved =
    typeof canSubmitExtra === "function" ? canSubmitExtra() : canSubmitExtra !== false;
  const canSubmit =
    mode === "unlock"
      ? currentPassword.trim().length > 0 && canSubmitExtraResolved
      : mode === "create"
        ? meetsPasswordPolicy(newPassword) && newPassword === confirmPassword && canSubmitExtraResolved
        : currentPassword.trim().length > 0 &&
          meetsPasswordPolicy(newPassword) &&
          newPassword === confirmPassword &&
          canSubmitExtraResolved;

  function showSnack(message) {
    setSnackbar(message);
    window.clearTimeout(showSnack._timer);
    showSnack._timer = window.setTimeout(() => setSnackbar(""), 5000);
  }

  function handleGeneratePassword() {
    const generated = generateCompliantPassword();
    setNewPassword(generated);
    setConfirmPassword(generated);
    setError("");
  }

  async function handleCopyPassword() {
    if (!newPassword) return;
    try {
      await navigator.clipboard.writeText(newPassword);
      showSnack(t("settings.password.copied", "Password copied"));
    } catch {
      setError(
        t(
          "settings.password.copyError",
          "We couldn’t copy the password. Please copy it manually.",
        ),
      );
    }
  }

  function schedulePostCreatePasswordSignOut() {
    if (postCreateSignOutTimerRef.current) {
      window.clearTimeout(postCreateSignOutTimerRef.current);
    }
    postCreateSignOutTimerRef.current = window.setTimeout(async () => {
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        // Password creation succeeded; redirect even if local session cleanup is already complete.
      } finally {
        nav("/login", { replace: true });
      }
    }, 1800);
  }

  function validateNewPassword() {
    if (!newPassword || !confirmPassword) {
      return t("settings.password.enterAndConfirm", "Enter and confirm your new password.");
    }
    if (newPassword.length < 8) {
      return t(
        "settings.password.minLength",
        "Use at least 8 characters with uppercase, lowercase, number and symbol.",
      );
    }
    if (newPassword !== confirmPassword) {
      return t("settings.password.mismatch", "Passwords do not match. Try again.");
    }
    return "";
  }

  async function verifyCurrentPassword() {
    if (!user?.email) {
      throw new Error("missing-email");
    }
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });
    if (signInError) throw signInError;
  }

  async function saveNewPassword() {
    const nextMeta = {
      ...(user?.user_metadata || {}),
      password_set: true,
      password_changed_at: new Date().toISOString(),
    };
    const { data: updateData, error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
      data: nextMeta,
    });
    if (updateError) {
      if (import.meta.env.DEV) {
        console.error("[AfriMed auth] updateUser({ password, data }) response", {
          data: updateData,
          error: getAuthErrorDebug(updateError),
        });
      }
      throw updateError;
    }
  }

  async function saveAccountSecurityPassword() {
    const result = await callFunctionRaw(
      "account-password-reset",
      { action: "set", password: newPassword, resetBasePath },
      { timeoutMs: 20000, preferFreshSession: true },
    );
    if (preserveAccountSessionAfterUpdate) {
      const email = normalizeStagingEmail(result?.email) || normalizeStagingEmail(userEmail);
      if (email) {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: newPassword,
        });
        if (signInError) throw signInError;
        await waitForPersistedSession();
      }
    }
    await auth?.refreshProfile?.();
  }

  function writeAccountResetStagingHandoff(email = "") {
    const normalizedEmail = normalizeStagingEmail(email);
    if (!normalizedEmail) return;
    writeCachedStagingAccess(normalizedEmail);
    try {
      sessionStorage.setItem(
        ACCOUNT_RESET_STAGING_HANDOFF_KEY,
        JSON.stringify({
          email: normalizedEmail,
          grantedAt: Date.now(),
        }),
      );
    } catch {
      // ignore storage failures
    }
  }

  async function refreshStagingAccess(email = "") {
    const normalizedEmail = normalizeStagingEmail(email);
    if (!normalizedEmail) return "";
    writeAccountResetStagingHandoff(normalizedEmail);
    try {
      const result = await checkStagingAccess(normalizedEmail);
      if (result.allowed) writeAccountResetStagingHandoff(result.email);
    } catch {
      // The reset-success handoff should still use the verified reset email.
    }
    return normalizedEmail;
  }

  async function waitForPersistedSession() {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.access_token && session?.user?.id) return session;
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    return null;
  }

  async function handleAccountResetSuccess(resetResult) {
    const email =
      normalizeStagingEmail(resetResult?.email) ||
      normalizeStagingEmail(auth?.user?.email) ||
      normalizeStagingEmail(resetEmail);
    if (email) {
      await refreshStagingAccess(email);
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: newPassword,
      });
      if (!signInError) {
        await waitForPersistedSession();
        writeAccountResetStagingHandoff(email);
        await auth?.refreshProfile?.();
      }
    } else {
      await auth?.refreshProfile?.();
    }
    nav(loginSecurityPath, { replace: true });
  }

  async function handleResetRequest(event) {
    event.preventDefault();
    const nextEmail = resetEmail.trim();
    if (!nextEmail || submitting) return;

    setSubmitting(true);
    setError("");
    try {
      await callFunctionRaw(
        "account-password-reset",
        { action: "request", email: nextEmail, resetBasePath },
        { timeoutMs: 20000, skipAuth: true },
      );
      setResetMode("sent");
    } catch {
      setError(
        t(
          "settings.password.accountReset.requestError",
          "We couldn’t send reset instructions right now. Please try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetConfirm(event) {
    event.preventDefault();
    if (submitting) return;

    const validationError = validateNewPassword();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!meetsPasswordPolicy(newPassword)) {
      setError(
        t(
          "settings.password.reset.confirm.policy",
          "Use at least 8 characters with uppercase, lowercase, number and symbol.",
        ),
      );
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      if (resetToken) {
        const resetResult = await callFunctionRaw(
          "account-password-reset",
          { action: "confirm", token: resetToken, password: newPassword, resetBasePath },
          { timeoutMs: 20000, skipAuth: true },
        );
        await handleAccountResetSuccess(resetResult);
      } else {
        await saveNewPassword();
        await auth?.refreshProfile?.();
        nav(loginSecurityPath, { replace: true });
      }
      setNewPassword("");
      setConfirmPassword("");
      showSnack(t("settings.password.updated", "Password updated"));
    } catch {
      setError(
        t(
          "settings.password.reset.confirm.saveError",
          "We couldn’t reset your password. Please request a new link and try again.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;

    setError("");
    setSubmitting(true);

    try {
      if (mode === "unlock") {
        await verifyCurrentPassword();
        setCurrentPassword("");
        onUnlocked?.();
        return;
      }

      const validationError = validateNewPassword();
      if (validationError) {
        setError(validationError);
        return;
      }

      if (!meetsPasswordPolicy(newPassword)) {
        setError(
          t(
            "settings.password.policy",
            "Use at least 8 characters with uppercase, lowercase, number and symbol.",
          ),
        );
        return;
      }

      if (mode === "change") {
        await verifyCurrentPassword();
      }

      if (typeof onPasswordSubmit === "function") {
        await onPasswordSubmit({
          mode,
          password: newPassword,
          confirmPassword,
          currentPassword,
          email: userEmail,
          user,
        });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        showSnack(
          onPasswordSubmitSuccessMessage ||
            t("settings.password.updated", "Password updated"),
        );
        return;
      } else if (context !== "ehr") {
        await saveAccountSecurityPassword();
      } else {
        await saveNewPassword();
      }
      setPasswordCreatedHere(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");

      if (mode === "create") {
        if (clearPasswordMigrationOnSuccess) {
          clearPasswordMigrationSignal(user);
        }
        if (typeof onCreatePasswordSuccess === "function") {
          await onCreatePasswordSuccess({
            user,
            email: userEmail,
          });
          return;
        }
        showSnack(t("settings.password.created", "Password created"));
        if (context !== "ehr") {
          schedulePostCreatePasswordSignOut();
          return;
        }
        onCreated?.();
        if (context === "ehr") onUnlocked?.();
        return;
      }

      if (clearPasswordMigrationOnSuccess) {
        clearPasswordMigrationSignal(user);
      }
      showSnack(t("settings.password.updated", "Password updated"));
    } catch (nextError) {
      logPasswordSaveError(nextError);
      setError(
        typeof onPasswordSubmit === "function" && nextError?.message
          ? nextError.message
          : mode === "change" || mode === "unlock"
          ? t(
              "settings.password.verifyError",
              "We could not verify your password. Please try again.",
            )
          : mapCreatePasswordSaveError(nextError, t),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (context !== "ehr" && resetMode === "request") {
    return (
      <div className={className}>
        <form className="space-y-6" onSubmit={handleResetRequest}>
          <div className="space-y-2">
            <h1 className="text-headline-small text-on-surface">
              {t("settings.password.accountReset.title", "Reset your password")}
            </h1>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.password.accountReset.subtitle",
                "Enter your email and we’ll send instructions to reset your password.",
              )}
            </p>
          </div>

          <TextField56
            label={t("settings.password.reset.emailLabel", "Email")}
            value={resetEmail}
            onChange={setResetEmail}
            autoComplete="email"
            inputMode="email"
            tone="surface"
            disabled={submitting}
          />

          {error ? (
            <div className="text-body-small text-[rgb(var(--afrimed-error))]">{error}</div>
          ) : null}

          <Button type="submit" fullWidth loading={submitting} disabled={!resetEmail.trim()}>
            {t("settings.password.reset.action", "Send reset instructions")}
          </Button>
        </form>
      </div>
    );
  }

  if (context !== "ehr" && resetMode === "sent") {
    return (
      <div className={className}>
        <div className="space-y-6">
          <Button type="button" fullWidth onClick={() => nav(loginSecurityPath)}>
            {t("settings.password.reset.backToAccountSecurity", "Back to Account & Security")}
          </Button>

          {showResetConfirmationHelper ? (
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.password.accountReset.confirmSubtitle",
                "We have sent you instructions to reset your password by email. Please check your inbox or spam folder.",
              )}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  if (context !== "ehr" && resetMode === "confirm") {
    return (
      <div className={className}>
        <form className="space-y-6" onSubmit={handleResetConfirm}>
          {userEmail ? (
            <input
              type="email"
              name="username"
              autoComplete="username"
              value={userEmail}
              readOnly
              hidden
            />
          ) : null}

          <div className="space-y-2">
            <h1 className="text-headline-small text-on-surface">
              {t("settings.password.reset.confirm.title", "Choose a new password")}
            </h1>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "settings.password.reset.confirm.subtitle",
                "Use at least 8 characters with uppercase, lowercase, number and symbol.",
              )}
            </p>
            <PasswordRequirementChecklist
              password={newPassword}
              className="[&_ul]:grid-cols-2 [&_ul]:gap-x-8"
            />
            <button
              type="button"
              className="text-body-medium text-primary"
              onClick={handleGeneratePassword}
              disabled={submitting}
            >
              {t("settings.password.generateAction", "Generate strong password")}
            </button>
          </div>

          <div className="space-y-4">
            <PasswordField
              id="account-security-reset-new-password"
              label={t("settings.password.reset.confirm.newLabel", "New password")}
              name="new-password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              disabled={submitting || !resetSessionReady}
            />
            <PasswordField
              id="account-security-reset-confirm-password"
              label={t("settings.password.reset.confirm.confirmLabel", "Confirm password")}
              name="confirm-password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              disabled={submitting || !resetSessionReady}
            />
          </div>

          {error ? (
            <div className="text-body-small text-[rgb(var(--afrimed-error))]">{error}</div>
          ) : null}

          <Button
            type="submit"
            fullWidth
            loading={submitting}
            disabled={
              !resetSessionReady ||
              !meetsPasswordPolicy(newPassword) ||
              newPassword !== confirmPassword
            }
          >
            {t("settings.password.reset.confirm.action", "Reset password")}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className={className}>
      {context !== "ehr" || mode === "create" ? (
        <div className="space-y-2">
          <div className={titleGroupClassName}>
            <h1 className={titleClassName}>{title}</h1>
            {copy ? <p className="text-body-medium text-on-surface-var">{copy}</p> : null}
            {mode === "create" && createSignOutNoticeOverride !== null ? (
              <p className="text-body-medium text-on-surface-var">
                {createSignOutNoticeOverride ||
                  t(
                    "settings.password.createSignOutNotice",
                    "After creating your password, you’ll be signed out. Please sign back in with your new password.",
                  )}
              </p>
            ) : null}
          </div>
          {mode !== "unlock" ? (
            <div className={requirementsRailClassName}>
              {requirementsPresentation === "sentence" ? (
                <PasswordRequirementSentence password={newPassword} />
              ) : (
                <PasswordRequirementChecklist
                  password={newPassword}
                  className="[&_ul]:grid-cols-2 [&_ul]:gap-x-8"
                />
              )}
              <button
                type="button"
                className={
                  requirementsPresentation === "sentence"
                    ? "mx-auto mt-3 block text-body-medium text-primary"
                    : "mt-2 text-body-medium text-primary"
                }
                onClick={handleGeneratePassword}
                disabled={submitting}
              >
                {t("settings.password.generateAction", "Generate strong password")}
              </button>
              {showCopyPasswordAction && newPassword ? (
                <button
                  type="button"
                  className={
                    requirementsPresentation === "sentence"
                      ? "mx-auto mt-2 block text-body-medium text-primary"
                      : "ml-4 mt-2 text-body-medium text-primary"
                  }
                  onClick={handleCopyPassword}
                  disabled={submitting}
                >
                  {t("settings.password.copyAction", "Copy password")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <form className={context === "ehr" ? "space-y-4" : "mt-7 space-y-5"} onSubmit={handleSubmit}>
        {context !== "ehr" && userEmail ? (
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={userEmail}
            readOnly
            hidden
          />
        ) : null}

        {leadingFormContent}

        {mode === "change" || mode === "unlock" ? (
          <PasswordField
            id={
              context === "ehr"
                ? "ehr-current-password"
                : "account-security-current-password"
            }
            label={currentLabel}
            name={mode === "unlock" ? "password" : "current-password"}
            value={currentPassword}
            onChange={setCurrentPassword}
            autoComplete="current-password"
            disabled={submitting}
          />
        ) : null}

        {mode === "create" || mode === "change" ? (
          <div className={context === "ehr" ? "space-y-4" : "space-y-5"}>
            <PasswordField
              id={
                context === "ehr"
                  ? "ehr-new-password"
                  : "account-security-new-password"
              }
              label={t("settings.password.newLabel", "New password")}
              name="new-password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              disabled={submitting}
            />
            <PasswordField
              id={
                context === "ehr"
                  ? "ehr-confirm-password"
                  : "account-security-confirm-password"
              }
              label={t("settings.password.confirmLabel", "Confirm password")}
              name="confirm-password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              disabled={submitting}
            />
          </div>
        ) : null}

        {error ? (
          <div className="text-body-small text-[rgb(var(--afrimed-error))]">{error}</div>
        ) : null}

        {trailingFormContent}

        {mode === "change" || mode === "unlock" ? (
          <button
            type="button"
            className="text-body-medium text-primary"
            onClick={() => {
              if (context === "ehr") {
                nav("/settings/patient/p/ehr/reset-password");
                return;
              }
              setResetMode("request");
              setError("");
            }}
          >
            {t("settings.password.forgotPassword", "Forgot password")}
          </button>
        ) : null}

        {mode === "create" || mode === "change" ? (
          <div
            className={
              submitRowClassName ||
              "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center"
            }
          >
            <Button
              type="submit"
              fullWidth
              className={secondarySubmitAction ? "gap-2 sm:flex-1" : "max-w-[720px] gap-2"}
              loading={submitting}
              disabled={!canSubmit}
            >
              {submitLeadingIcon}
              {cta}
            </Button>
            {secondarySubmitAction}
          </div>
        ) : (
          <Button type="submit" fullWidth loading={submitting} disabled={!canSubmit}>
            {cta}
          </Button>
        )}
      </form>

      <Snackbar open={!!snackbar} onClose={() => setSnackbar("")}>
        {snackbar}
      </Snackbar>
    </div>
  );
}
